import { isAxiosError } from 'axios';
import { Mail } from 'lucide-react';
import { useState } from 'react';

import { useSendWeeklyReport } from '../../hooks/useWeeklyReports';
import { numberFormat } from '../../pages/weeklyreport/format';
import { parseEmails } from '../../utils/email';
import { Modal } from '../agents/Modal';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

const DEFAULT_RECIPIENT = 'emil.hakanzon@careofcarl.com';

const SEND_ERROR_COPY: Record<string, string> = {
  no_recipients:
    'Inga mottagare angivna. Skriv in minst en adress eller konfigurera standardmottagare i systemet.',
  invalid_body: 'Ogiltig förfrågan – kontrollera e-postadresserna.',
  send_failed: 'Utskicket misslyckades till samtliga mottagare.',
  already_sent: 'Rapporten är redan skickad för den här veckan.',
};

const getSendErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    const code = (err.response?.data as { error?: string } | undefined)?.error;
    if (code && SEND_ERROR_COPY[code]) return SEND_ERROR_COPY[code];
    if (err.response?.status === 502) return SEND_ERROR_COPY.send_failed;
  }
  return 'Något gick fel vid utskicket. Försök igen.';
};

interface SendReportModalProps {
  weekLabel: string;
  isoYear: number;
  isoWeek: number;
  greetingsCount: number;
  excludeGreetings: boolean;
  onClose: () => void;
}

export const SendReportModal = ({
  weekLabel,
  isoYear,
  isoWeek,
  greetingsCount,
  excludeGreetings,
  onClose,
}: SendReportModalProps) => {
  const sendReport = useSendWeeklyReport();
  const [recipientsInput, setRecipientsInput] = useState(DEFAULT_RECIPIENT);
  const [fieldError, setFieldError] = useState('');

  const alreadySent = !!sendReport.data && 'alreadySent' in sendReport.data;
  const sendSucceeded = !!sendReport.data && 'ok' in sendReport.data;

  const close = () => {
    sendReport.reset();
    setFieldError('');
    onClose();
  };

  const send = (force: boolean) => {
    const { valid, invalid } = parseEmails(recipientsInput);
    if (invalid.length) {
      setFieldError(`Ogiltiga adresser: ${invalid.join(', ')}`);
      return;
    }
    setFieldError('');
    sendReport.mutate({
      year: isoYear,
      week: isoWeek,
      force,
      recipients: valid,
      excludeGreetings,
    });
  };

  return (
    <Modal
      title="Skicka veckorapport?"
      onClose={close}
      footer={
        sendSucceeded && !alreadySent ? (
          <Button variant="primary" size="sm" onClick={close}>
            Stäng
          </Button>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={close}>
              Avbryt
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={sendReport.isPending}
              onClick={() => send(alreadySent)}
            >
              {alreadySent ? 'Skicka ändå' : 'Skicka'}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          <strong>{weekLabel}</strong> skickas som e-post. Lämna fältet tomt för att använda
          systemets standardmottagare.
        </p>

        {greetingsCount > 0 && (
          <p className="text-sm text-slate-500">
            Mejlet skickas i samma läge som du ser nu:{' '}
            <strong>
              {excludeGreetings
                ? `utan de ${numberFormat.format(greetingsCount)} hälsningschattarna`
                : 'med alla chattar'}
            </strong>
            . Mottagaren kan inte växla i ett mejl, så läget skrivs ut i rapporten.
          </p>
        )}

        <Input
          label="Mottagare (kommaseparerade)"
          placeholder="namn@careofcarl.se, chef@careofcarl.se"
          value={recipientsInput}
          onChange={(e) => setRecipientsInput(e.target.value)}
          error={fieldError || undefined}
          icon={<Mail className="h-4 w-4" />}
        />

        {sendReport.data && 'alreadySent' in sendReport.data && (
          <Alert
            type="warning"
            message={`Redan skickad ${new Date(sendReport.data.sentAt).toLocaleString('sv-SE')}. Vill du skicka igen?`}
          />
        )}
        {sendReport.data && 'ok' in sendReport.data && (
          <>
            <Alert
              type="success"
              message={`Skickad till ${sendReport.data.recipients.join(', ')}.`}
            />
            {sendReport.data.failed && sendReport.data.failed.length > 0 && (
              <Alert
                type="warning"
                message={`Kunde inte skicka till: ${sendReport.data.failed.map((f) => f.to).join(', ')}.`}
              />
            )}
          </>
        )}
        {sendReport.isError && (
          <Alert type="error" message={getSendErrorMessage(sendReport.error)} />
        )}
      </div>
    </Modal>
  );
};
